import { z } from "zod";
import { BANK_ACCOUNT_TYPE, RECORD_STATUS } from "../enums.js";
import { accountNumber, ifsc, listQuery, money, note, objectId, optionalEmail, optionalIndianMobile, } from "./common.js";
/* -------------------------------------------------------------------------- */
/* Bank                                                                       */
/* -------------------------------------------------------------------------- */
/**
 * A Bank is the institution (HDFC, ICICI, SBI). It holds NO balance — money lives in the
 * accounts beneath it. Keeping the institution separate is what lets "HDFC" be spelled
 * one way on every report.
 */
export const createBankSchema = z.object({
    name: z.string().trim().min(2, "Bank name is required").max(120),
    shortName: z
        .string()
        .trim()
        .toUpperCase()
        .max(20)
        .optional()
        .transform((v) => (v ? v : undefined)),
    ifscPrefix: z
        .union([z.string().trim().toUpperCase().regex(/^[A-Z]{4}$/, "The IFSC prefix is 4 letters"), z.literal("")])
        .optional()
        .transform((v) => (v ? v : undefined)),
    /** Bank-wide contact, distinct from the per-account branch contact. */
    contactPerson: z.string().trim().max(80).optional(),
    phone: optionalIndianMobile,
    email: optionalEmail,
    status: z.nativeEnum(RECORD_STATUS).default("ACTIVE"),
    notes: note(),
});
export const updateBankSchema = createBankSchema.partial();
/* -------------------------------------------------------------------------- */
/* Bank account                                                               */
/* -------------------------------------------------------------------------- */
/** A real bank account. One HDFC current account is one account with one balance. */
export const createBankAccountSchema = z.object({
    bankId: objectId,
    accountName: z.string().trim().min(2, "Account name is required").max(120),
    accountNumber,
    ifsc,
    bankBranchName: z.string().trim().max(120).optional(),
    accountType: z.nativeEnum(BANK_ACCOUNT_TYPE).default("CURRENT"),
    /**
     * Posted as an OPENING_BALANCE transaction against equity, never stored as a mutable
     * field. It can be negative for an overdraft account that starts drawn down.
     */
    openingBalance: money.default(0),
    openingDate: z.coerce.date().optional(),
    /**
     * Overdraft limit, as a positive number.
     *
     * The balance check permits the account to go this far below zero. A CURRENT account
     * with no limit cannot be overdrawn at all, which is what stops a payment being posted
     * from an account that does not have the money.
     */
    overdraftLimit: money.default(0).refine((v) => v >= 0, "The overdraft limit is entered as a positive amount"),
    /** Warn on the dashboard when the balance falls below this. */
    lowBalanceThreshold: money.default(0),
    status: z.nativeEnum(RECORD_STATUS).default("ACTIVE"),
    notes: note(),
});
/**
 * Account number, IFSC and bank are all immutable after creation.
 *
 * They identify the real-world account that historical entries were posted against.
 * Editing one would silently re-point months of reconciled transactions at a different
 * account — if the details were entered wrongly, the correct action is to close this
 * account and open the right one, leaving the audit trail intact.
 */
export const updateBankAccountSchema = createBankAccountSchema
    .omit({
    bankId: true,
    accountNumber: true,
    ifsc: true,
    openingBalance: true,
    openingDate: true,
})
    .partial();
/* -------------------------------------------------------------------------- */
/* Opening balance correction                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Correct a bank account's or drawer's opening balance.
 *
 * Accepted only while nothing but the opening has been posted to the account — see
 * `OpeningBalanceState.editable`. The amount is in the account's own terms, exactly as on
 * create: negative only for a bank account that starts overdrawn.
 */
export const changeOpeningBalanceSchema = z.object({
    openingBalance: money,
    reason: z.string().trim().max(300).optional(),
});
export const bankAccountQuerySchema = listQuery.extend({
    bankId: objectId.optional(),
    accountType: z.nativeEnum(BANK_ACCOUNT_TYPE).optional(),
    status: z.nativeEnum(RECORD_STATUS).optional(),
});
/* -------------------------------------------------------------------------- */
/* Cash account                                                               */
/* -------------------------------------------------------------------------- */
/**
 * A cash drawer.
 *
 * ORGANISATION-WIDE, like every other account here: a drawer is named and counted on its
 * own, and any counter may settle through it. Several are supported for a business
 * running separate counters that tally independently.
 */
export const createCashAccountSchema = z.object({
    name: z.string().trim().min(2, "Name is required").max(120),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .max(20)
        .optional()
        .transform((v) => (v ? v : undefined)),
    openingBalance: money.default(0),
    openingDate: z.coerce.date().optional(),
    /**
     * Cash cannot be negative — you cannot pay out money that is not in the drawer. This is
     * enforced on posting, unlike a bank account which may have an overdraft facility.
     */
    status: z.nativeEnum(RECORD_STATUS).default("ACTIVE"),
    notes: note(),
});
export const updateCashAccountSchema = createCashAccountSchema
    .omit({ openingBalance: true, openingDate: true })
    .partial();
//# sourceMappingURL=banking.js.map